import type { GetUserReferralsResponse } from '@lyka-pet-food/contracts'
import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import { ErrorResponseSchema } from './general'
import { Profanity } from '@2toad/profanity'

const c = initContract()
// `wholeWord: false` so substring matches like `boobs10` are caught. Default
// `wholeWord: true` only flags whole-word hits, which is wrong for arbitrary
// user-chosen codes.
const profanity = new Profanity({ wholeWord: false })

export const ReferralSchema = z.object({
  promotionId: z.string().uuid(),
  code: z.string(),
  url: z.string().url(),
})

export const UpdateReferralCodeBodySchema = z.object({
  oldPromotionId: z.string().uuid(),
  code: z
    .string()
    .trim()
    .min(1, 'Referral code is required')
    .max(32, 'Referral code must be 32 characters or fewer')
    .regex(/^[a-zA-Z0-9_-]+$/, 'Only letters, numbers, dashes, and underscores are allowed')
    .refine((text) => !profanity.exists(text), {
      message: 'Remove inappropriate language',
    }),
})

export type Referral = z.infer<typeof ReferralSchema>
export type UpdateReferralCodeBody = z.infer<typeof UpdateReferralCodeBodySchema>
export type { GetUserReferralsResponse }

export const referralContract = c.router(
  {
    getReferralCode: {
      method: 'GET',
      path: '/referral/code',
      responses: {
        200: ReferralSchema,
        404: ErrorResponseSchema,
      },
    },
    updateReferralCode: {
      method: 'PATCH',
      path: '/referral/code',
      body: UpdateReferralCodeBodySchema,
      responses: {
        200: ReferralSchema,
        404: ErrorResponseSchema,
        409: ErrorResponseSchema,
      },
    },
    getReferralHistory: {
      method: 'GET',
      path: '/referral/history',
      responses: {
        200: c.type<GetUserReferralsResponse>(),
        404: ErrorResponseSchema,
      },
    },
  },
  {
    commonResponses: {
      400: ErrorResponseSchema,
      401: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },
)
